import {
  IBaseTyreOffer,
  ISupplierWithOffers,
  NillishObject,
  NullishRecord,
} from '@wyz/types';
import { isEmptyValue, parseFormData } from '@wyz/utils';
import { isNil } from 'lodash-es';
import { TYRE_SEARCH_DEFAULT_VALUES } from '~/purchase/helpers';
import { SEARCH_TYPE_ENUM, TYRE_SPEC_ENUM } from '~/purchase/schemas';
import {
  IBaseProductSearchQs,
  IOffer,
  IBaseFilter,
  IProductListState,
  IBaseSearchFormSchema,
  ISpecificityFilter,
  FilterFields,
} from '~/purchase/types';

type Args = NullishRecord<{
  identifier: string;
  width: number;
  height: number;
  diameter: number;
  loadIndex: number;
  speedIndex: string;
}>;

export const formatDimension = (values: Args) => {
  if (!isNil(values.identifier)) {
    return values.identifier;
  } else if (values.width && values.height && values.diameter) {
    return `${values.width}/${values.height} R${values.diameter} ${
      values.loadIndex || '-'
    }${values.speedIndex || '-'}`;
  } else {
    return undefined;
  }
};

type IFilters<T extends IBaseTyreOffer> = {
  [Key in FilterFields]: (
    products: T[],
    value: {
      filters: IProductListState['filters'];
      searchQuery: IProductListState['searchQuery'];
    },
    searchType?: (typeof SEARCH_TYPE_ENUM)[keyof typeof SEARCH_TYPE_ENUM],
  ) => T[];
};

const getSpecificityFilters = (): {
  [Key in ISpecificityFilter]: (tyreOffer: IBaseTyreOffer) => boolean;
} => ({
  electric: ({ tyre }) =>
    tyre.specification.electricId !== TYRE_SPEC_ENUM.ECO.DISABLED,
  eco: ({ tyre }) => tyre.specification.eco !== TYRE_SPEC_ENUM.ECO.DISABLED,
  sport: ({ tyre }) => tyre.specification.sportCompatibility,
  studdable: ({ tyre }) =>
    tyre.specification.typeTyre === 'complete wheel studded',
  studded: ({ tyre }) => tyre.specification.typeTyre === 'studded',
  reinforced: ({ tyre }) => !isNil(tyre.dimension.rfd),
  sealRunFlat: ({ tyre }) =>
    tyre.specification.seal === 'Y' || tyre.specification.runFlat,
  campingCar: ({ tyre }) => tyre.specification.campingCar === 1,
  promotion: (tyreOffer) => tyreOffer.suppliers.length !== 0,
  pmsf: ({ tyre }) => tyre.season.pmsf === 'Y',
});

const getProductFilters = <T extends IBaseTyreOffer>(): IFilters<T> => ({
  segments: (products, { filters }) => {
    return filters.segments.length === 0
      ? products
      : products.filter(({ tyre }) =>
          filters.segments.includes(tyre.segment.publicKey),
        );
  },
  brands: (products, { filters }) =>
    filters.brands.length === 0
      ? products
      : products.filter(({ tyre }) => {
          return filters.brands.includes(tyre.brand.publicKey);
        }),
  seasons: (products, { filters }) =>
    filters.seasons.length === 0
      ? products
      : products.filter(({ tyre }) => {
          return filters.seasons.some((season) => tyre.season.id === season);
        }),
  vehicleTypes: (products, { filters }) =>
    filters.vehicleTypes.length === 0
      ? products
      : products.filter(({ tyre }) =>
          filters.vehicleTypes.includes(tyre.vehicleType.publicKey),
        ),
  homologations: (products, { filters }) => {
    if (filters.homologations.length === 0) {
      return products;
    }

    return products.filter(({ tyre }) => {
      const tyreHomologationBrands =
        isNil(tyre.homologations) || tyre.homologations.length === 0
          ? ['']
          : tyre.homologations.map(({ brand }) => brand);

      return tyreHomologationBrands.some((brand) =>
        filters.homologations.includes(brand),
      );
    });
  },
  availability: (products, { filters, searchQuery }, searchType) =>
    filters.availability.length === 0
      ? products
      : products.filter((tyreOffer) => {
          const requestedQuantity = searchQuery?.quantity ?? 0;
          const productQuantity = tyreOffer.totalAvailableQuantity;
          const conditions = {
            total: productQuantity >= requestedQuantity,
            partial: productQuantity > 0 && productQuantity < requestedQuantity,
            unavailable:
              tyreOffer.status === 'EMPTY_STOCK' ||
              (productQuantity === 0 && searchType === SEARCH_TYPE_ENUM.QUOTE
                ? true
                : tyreOffer.status !== 'TECHNICAL_ERROR'),
            error: tyreOffer.status === 'TECHNICAL_ERROR',
          };
          for (const availability of filters.availability) {
            if (conditions[availability]) {
              return true;
            }
          }
          return false;
        }),
  specificities: (products, { filters }) => {
    if (filters.specificities.length === 0) {
      return products;
    }
    let result = [...products];
    if (filters.specificities.includes('promotion')) {
      // remove suppliers that don't have a discount, so we can filter by suppliers length
      result = products.map((product) => ({
        ...product,
        suppliers: product.suppliers
          .map((supplier) => ({
            ...supplier,
            offers: supplier.offers.filter(
              (offer) => offer.discountPercentage !== 0,
            ),
          }))
          .filter((supplier) => supplier.offers.length === 0),
      }));
    }
    return result.filter((product) => {
      for (const specificity of filters.specificities) {
        if (getSpecificityFilters()[specificity](product)) {
          return true;
        }
      }
      return false;
    });
  },
  j0: (products, { filters }) =>
    !filters.j0
      ? products
      : products.reduce<T[]>((result, product) => {
          const suppliers = product.suppliers
            .map((supplier) => ({
              ...supplier,
              offers: supplier.offers.filter((offer) => offer.isJ0),
            }))
            .filter((supplier) => supplier.offers.length > 0);

          return suppliers.length > 0
            ? [...result, { ...product, suppliers }]
            : result;
        }, []),
});

export function filterProducts<T extends IBaseTyreOffer = IBaseTyreOffer>(
  products: T[],
  listState: IProductListState,
  searchType?: (typeof SEARCH_TYPE_ENUM)[keyof typeof SEARCH_TYPE_ENUM],
) {
  let result = [...products];
  Object.values(getProductFilters<T>()).forEach((filterer) => {
    result = filterer(
      result,
      {
        filters: listState.filters,
        searchQuery: listState.searchQuery,
      },
      searchType,
    );
  });

  return result;
}

export const hasMinimalSearchParams = (
  values: IBaseSearchFormSchema,
): boolean => {
  return (
    !isEmptyValue(values.identifier?.value) ||
    (!isEmptyValue(values.width) &&
      !isEmptyValue(values.height) &&
      !isEmptyValue(values.diameter) &&
      !isEmptyValue(values.quantity))
  );
};

export function sortProducts(
  products: IBaseTyreOffer[],
  listState: IProductListState,
) {
  const result = [...products];
  const sort = listState.sort;
  if (!sort || sort === 'relevance') {
    return result;
  }

  let getters: Array<{
    getter: (product: IBaseTyreOffer) => number;
    asc: boolean;
  }> = [];

  if (sort === 'price') {
    getters = [
      {
        getter: (product) => {
          let result = Infinity;
          for (const supplier of product.suppliers) {
            for (const offer of supplier.offers) {
              if (!!offer.displayPrice && !!offer.availableQuantity) {
                result = offer.displayPrice;
                break;
              }
            }
          }
          return result;
        },
        asc: true,
      },
      {
        getter: (product) =>
          product.suppliers[0]?.offers?.[0]?.availableQuantity || 0,
        asc: false,
      },
      {
        getter: (product) =>
          product.tyreCreatedAt ? new Date(product.tyreCreatedAt).getTime() : 0,
        asc: false,
      },
    ];
  } else {
    getters = [
      {
        getter: (product) => {
          const date = product.suppliers[0]?.offers[0]?.deliveryDate;
          return date ? new Date(date).getTime() : Infinity;
        },
        asc: true,
      },
    ];
  }

  return result.sort((product1, product2) => {
    for (const { getter, asc } of getters) {
      const diff = getter(product1) - getter(product2);
      if (diff !== 0 && !isNaN(diff)) {
        return asc ? diff : diff * -1;
      }
    }
    return 0;
  });
}

export const mapOffers = (suppliers: ISupplierWithOffers[]): Array<IOffer> => {
  let offers: Array<IOffer> = suppliers
    .map(
      ({ supplier, offers }) =>
        offers.map(
          (offer) =>
            ({
              offerMetadata: offer,
              supplier,
            }) satisfies IOffer,
        ) ?? [],
    )
    .flat();
  const expressOffers = offers.filter(
    ({ offerMetadata }) => offerMetadata.isJ0,
  );
  if (expressOffers.length !== 0) {
    offers = [
      ...expressOffers,
      ...offers.filter(({ offerMetadata }) => !offerMetadata.isJ0),
    ];
  }
  return offers;
};

export const parseSearchFromQs = (
  searchParams: URLSearchParams,
): Partial<IBaseSearchFormSchema> => {
  const parsedQueryParams = parseFormData<IBaseProductSearchQs>(
    searchParams.toString(),
  );

  const overrides: Partial<IBaseSearchFormSchema> = {};
  if (parsedQueryParams.identifier) {
    overrides.identifier = {
      label: decodeURIComponent(parsedQueryParams.identifier),
      value: decodeURIComponent(parsedQueryParams.identifier),
    };
    overrides.fieldNameSearch =
      parsedQueryParams.fieldNameSearch ?? 'DIMENSION';
  }
  (['width', 'height', 'diameter'] as const).forEach((field) => {
    if (!isEmptyValue(parsedQueryParams[field])) {
      overrides[field] = parsedQueryParams[field];
    }
  });
  overrides.loadIndexes = parsedQueryParams.loadIndexes
    ? parsedQueryParams.loadIndexes
    : [];
  overrides.speedIndexes = parsedQueryParams.speedIndexes
    ? parsedQueryParams.speedIndexes
    : [];
  overrides.seasons = parsedQueryParams.seasons
    ? parsedQueryParams.seasons
    : [];
  overrides.brands = parsedQueryParams.brands ? parsedQueryParams.brands : [];
  overrides.quantity = parsedQueryParams.quantity ?? undefined;
  overrides.vehicleTypes = parsedQueryParams.vehicleTypes ?? [];
  overrides.includeHigherIndexes = Boolean(
    parsedQueryParams.includeHigherIndexes,
  );

  return overrides;
};

export const serializeSearchToQs = (
  values?: NillishObject<IBaseSearchFormSchema>,
): NillishObject<IBaseProductSearchQs> => {
  if (!values) {
    return {};
  }
  const { identifier, fieldNameSearch, ...rest } = values;
  const searchParams: NillishObject<IBaseProductSearchQs> = {
    ...rest,
  };
  if (identifier) {
    searchParams.identifier = encodeURIComponent(identifier.value);
    searchParams.fieldNameSearch = fieldNameSearch;
  }
  return searchParams;
};

export const getTyreSearchDefaultValues = (
  overrides?: Partial<IBaseSearchFormSchema>,
) => ({
  ...TYRE_SEARCH_DEFAULT_VALUES,
  ...overrides,
});

const DEFAULT_AVAILABILITY: IBaseFilter['availability'] = ['total', 'partial'];
const DEFAULT_FILTERS: IBaseFilter = {
  segments: [],
  brands: [],
  seasons: [],
  vehicleTypes: [],
  homologations: [],
  availability: DEFAULT_AVAILABILITY,
  specificities: [],
  j0: false,
};

export const DEFAULT_SEARCH_SORT = 'relevance';
export const DEFAULT_PRODUCT_LIST_STATE: IProductListState = {
  searchQuery: undefined,
  filters: DEFAULT_FILTERS,
  sort: DEFAULT_SEARCH_SORT,
};

export const getInitialSearchMode = (
  searchQuery?: IBaseSearchFormSchema,
): 'fast' | 'manual' => {
  if (!searchQuery) {
    return 'fast';
  }

  if (!isEmptyValue(searchQuery.identifier?.value)) {
    return 'fast';
  }

  if (
    !isEmptyValue(searchQuery.width) &&
    !isEmptyValue(searchQuery.height) &&
    !isEmptyValue(searchQuery.diameter)
  ) {
    return 'manual';
  }

  return 'fast';
};

export const getFilterCount = <T extends FilterFields = FilterFields>(
  products: IBaseTyreOffer[],
  listState: IProductListState,

  filter: T,
  value: IBaseFilter[T],
): number => {
  let availability: IBaseFilter['availability'];
  // when no availability filter is selected, we force total and partial
  if (listState.filters.availability.length === 0) {
    availability = ['total', 'partial'];
  } else {
    // if filter is not empty, we only keep total / partial values
    availability = listState.filters.availability.filter(
      (filter) => filter === 'total' || filter === 'partial',
    );
    // if total / partial is not selected, we return 0
    if (availability.length === 0 && filter !== 'availability') {
      return 0;
    }
  }
  return filterProducts(products, {
    ...listState,
    filters: {
      ...listState.filters,
      availability,
      // generic filter must be after availability, so it will be overridden when we want to count availability
      [filter]: value,
    },
  }).length;
};
